import { useMemo, useState } from 'react'
import { Info, Plus } from 'lucide-react'
import {
  useAdminRetellPhones,
  type AdminRetellPhone,
  type AdminRetellPhoneAgent,
  type RetellPhoneAgentKind,
} from '../../api/admin-retell-phones'
import {
  useAdminOrgsProvisioning,
  type AdminOrgProvisioningRow,
} from '../../api/admin-phones'
import { AddRetellPhoneModal } from './AddRetellPhoneModal'
import { AddRetellAgentBindingModal } from './AddRetellAgentBindingModal'

/**
 * Retell phone inventory — superadmin-only sibling of the VAPI inventory
 * section in :file:`AdminBillingPhonesTab.tsx`.
 *
 * Layout: org-selector at the top, then a card per registered Retell phone
 * showing its e164, label, termination URI, and bound agents. The org list
 * is reused from ``useAdminOrgsProvisioning()`` — the VAPI section is
 * already fetching it, so this hook is served from cache without a second
 * round-trip.
 *
 * Why a selector instead of "all orgs" like VAPI? The Retell backend
 * endpoint is per-org by design (``GET /api/admin/orgs/{org_id}/retell-phones``)
 * and there's no aggregate-list equivalent. Showing all orgs would either
 * require an N-fan-out client-side or a new backend endpoint — both are
 * out of scope for this UI-only PR. Orgs whose ``provider`` is still
 * ``'vapi'`` are still listed because a pre-flip Retell-phone binding is
 * a valid state (operator wires the phone before flipping the org's
 * provider).
 *
 * Step-up MFA on every mutation is handled centrally in
 * :file:`api/client.ts:43-49` — see ``AdminBillingPhonesTab`` for the
 * rationale on not adding optimistic UI here.
 */
export function RetellPhoneInventorySection() {
  const orgs = useAdminOrgsProvisioning()
  const [orgId, setOrgId] = useState<string>('')

  // Stable alpha sort for the dropdown so operators can scan the list.
  const sortedOrgs = useMemo(
    () =>
      [...(orgs.data?.orgs ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [orgs.data?.orgs],
  )

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <div className="font-medium">Retell phone inventory</div>
          <div className="text-xs text-text-secondary">
            Per-org Retell phones + agent bindings. Used after flipping an
            org to <code>provider=retell</code>.
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-text-secondary">Org</span>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1 text-sm"
            disabled={orgs.isLoading}
          >
            <option value="">
              {orgs.isLoading ? 'Loading orgs…' : 'Pick an org…'}
            </option>
            {sortedOrgs.map((o) => (
              <option key={o.org_id} value={o.org_id}>
                {o.name}
                {o.slug ? ` · ${o.slug}` : ''}
              </option>
            ))}
          </select>
        </label>
      </header>

      {orgId === '' ? (
        <EmptyOrgState />
      ) : (
        <OrgRetellInventory
          orgId={orgId}
          orgRow={sortedOrgs.find((o) => o.org_id === orgId) ?? null}
        />
      )}
    </section>
  )
}

function EmptyOrgState() {
  return (
    <div className="flex items-start gap-3 p-4 text-sm text-text-secondary">
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div>
        Pick an org above to view or provision its Retell phones. Phones are
        added one at a time; bindings tell the dispatcher which Retell agent
        answers outbound vs inbound calls on that number.
      </div>
    </div>
  )
}

interface OrgRetellInventoryProps {
  orgId: string
  orgRow: AdminOrgProvisioningRow | null
}

function OrgRetellInventory({ orgId, orgRow }: OrgRetellInventoryProps) {
  const phones = useAdminRetellPhones(orgId)
  const [addingPhone, setAddingPhone] = useState(false)

  if (phones.isLoading) {
    return (
      <div className="p-6 text-center text-sm text-text-secondary">
        Loading…
      </div>
    )
  }
  if (phones.isError) {
    return (
      <div className="m-4 rounded border border-error/30 bg-error/5 p-3 text-xs text-error">
        Failed to load Retell phones: {String(phones.error)}
      </div>
    )
  }

  const phoneRows = phones.data?.phones ?? []
  const orgName = orgRow?.name ?? orgId

  return (
    <div className="space-y-3 p-4">
      {phoneRows.length === 0 ? (
        <div className="rounded border border-dashed border-border bg-background p-4 text-sm text-text-secondary">
          No Retell phones bound for this org yet.
        </div>
      ) : (
        <ul className="space-y-3">
          {phoneRows.map((phone) => (
            <li key={phone.id}>
              <RetellPhoneCard orgId={orgId} phone={phone} />
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={() => setAddingPhone(true)}
        className="inline-flex items-center gap-1 rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-surface-elevated"
      >
        <Plus className="h-3.5 w-3.5" /> Add Retell phone
      </button>

      {addingPhone && (
        <AddRetellPhoneModal
          orgId={orgId}
          orgName={orgName}
          onClose={() => setAddingPhone(false)}
        />
      )}
    </div>
  )
}

interface RetellPhoneCardProps {
  orgId: string
  phone: AdminRetellPhone
}

function RetellPhoneCard({ orgId, phone }: RetellPhoneCardProps) {
  const [bindingAgent, setBindingAgent] = useState(false)

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-border bg-surface-elevated px-4 py-2">
        <div>
          <div className="font-mono text-sm">{phone.e164}</div>
          <div className="text-xs text-text-secondary">
            {phone.label ?? '(no label)'}
          </div>
        </div>
        {phone.termination_uri && (
          <div className="text-xs">
            <div className="text-text-secondary">Termination</div>
            <code className="block break-all font-mono text-xs">
              {phone.termination_uri}
            </code>
          </div>
        )}
      </header>

      <div className="space-y-2 px-4 py-3">
        <div className="text-xs uppercase tracking-wide text-text-secondary">
          Bound agents
        </div>
        {phone.agents.length === 0 ? (
          <div className="text-xs text-text-secondary">
            No agents bound yet. Add one to enable dispatch on this phone.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {phone.agents.map((agent) => (
              <li key={agent.id} className="py-2">
                <RetellAgentRow agent={agent} />
              </li>
            ))}
          </ul>
        )}
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setBindingAgent(true)}
            className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-elevated"
          >
            <Plus className="h-3 w-3" /> Add agent binding
          </button>
        </div>
      </div>

      {bindingAgent && (
        <AddRetellAgentBindingModal
          orgId={orgId}
          phoneId={phone.id}
          phoneE164={phone.e164}
          onClose={() => setBindingAgent(false)}
        />
      )}
    </div>
  )
}

function RetellAgentRow({ agent }: { agent: AdminRetellPhoneAgent }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
      <div className="min-w-0">
        <div className="font-mono text-xs break-all">{agent.agent_id}</div>
        {agent.purpose && (
          <div className="text-xs text-text-secondary">
            purpose: <code>{agent.purpose}</code>
          </div>
        )}
      </div>
      <KindChip kind={agent.kind} />
    </div>
  )
}

function KindChip({ kind }: { kind: RetellPhoneAgentKind }) {
  // Match the VAPI tab's KindChip palette so operators don't have to relearn
  // the meaning when they look at both sections side-by-side.
  const cls =
    kind === 'inbound'
      ? 'border-primary/40 bg-primary/10 text-primary'
      : kind === 'outbound'
        ? 'border-success/40 bg-success/10 text-success'
        : 'border-border bg-background text-text-secondary'
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${cls}`}
    >
      {kind}
    </span>
  )
}
